import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Checkbox,
  Notice,
  RadioCard,
  SelectField,
  TextField,
  iconCopy,
  iconWhatsapp,
  useSnackbar,
} from '@sauda/ui/kit';
import type { Role } from '@sauda/shared';
import { LinkButton } from '../../app/LinkButton.js';
import { useAuth } from '../../auth/AuthContext.js';
import { ApiError, api } from '../../lib/api.js';
import { PUBLIC_NAME } from '../../lib/config.js';
import { inviteMessage, whatsappShareUrl } from '../../lib/invite-share.js';
import type { Branch, CreatedInvite } from '../../lib/types.js';

/** Invite someone: number, role as cards, branch; then the link to share from the broker's own WhatsApp. */
export function InviteSheet({
  open,
  onClose,
  roles,
  branches,
  defaultBranchId,
}: {
  open: boolean;
  onClose: () => void;
  roles: Role[];
  branches: Branch[];
  defaultBranchId: string;
}) {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<Role>('agent');
  const [branchId, setBranchId] = useState('');
  const [notify, setNotify] = useState(false);
  const [attested, setAttested] = useState(false);
  const [created, setCreated] = useState<CreatedInvite | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () =>
      api<CreatedInvite>('/invites', {
        method: 'POST',
        body: {
          phone,
          role,
          branchId: branchId === '' ? defaultBranchId : branchId,
          notifyOnWhatsapp: notify,
          whatsappConsentAttested: notify && attested,
        },
      }),
    onSuccess: async (result) => {
      setCreated(result);
      setError(null);
      await cache.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.code === 'PLAN_LIMIT' ? t('team.planLimit') : t('team.generic'),
      ),
  });
  const close = () => {
    onClose();
    setCreated(null);
    setPhone('');
    setNotify(false);
    setAttested(false);
    setError(null);
  };
  const share = {
    inviter: t('team.yourTeam'),
    agency: auth.current?.agencyName ?? '',
    app: PUBLIC_NAME,
    link: created?.link ?? '',
    locale: i18n.language,
  };
  return (
    <BottomSheet
      open={open}
      onClose={close}
      label={created === null ? t('team.invite') : t('team.inviteReady')}
      title={created === null ? t('team.invite') : t('team.inviteReady')}
      closeLabel={t('app.close')}
    >
      {created === null ? (
        <form
          className="form-stack"
          onSubmit={(e) => {
            e.preventDefault();
            invite.mutate();
          }}
        >
          <TextField
            label={t('team.phone')}
            type="tel"
            inputMode="tel"
            prefix="+91"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
          <fieldset className="form-stack">
            <legend className="k-t-label">{t('team.role')}</legend>
            {roles.map((r) => (
              <RadioCard
                key={r}
                name="role"
                label={t(`roles.${r}`)}
                description={t(`team.roleHelp_${r}`)}
                checked={role === r}
                onChange={() => setRole(r)}
              />
            ))}
          </fieldset>
          <SelectField
            label={t('team.branch')}
            value={branchId === '' ? defaultBranchId : branchId}
            onChange={(e) => setBranchId(e.target.value)}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
          />
          <Checkbox
            label={t('team.notify')}
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
          />
          {notify ? (
            <Checkbox
              label={t('team.attest')}
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
            />
          ) : null}
          {error === null ? null : <Notice tone="error">{error}</Notice>}
          <Button
            type="submit"
            size="lg"
            block
            loading={invite.isPending}
            disabled={phone.trim() === '' || (notify && !attested)}
          >
            {t('team.createInvite')}
          </Button>
        </form>
      ) : (
        <div className="form-stack">
          <p className="k-t-body">{t('team.shareHelp')}</p>
          <p className="k-t-body k-muted k-wrap-anywhere" data-testid="invite-link">
            {created.link}
          </p>
          <LinkButton icon={iconWhatsapp} href={whatsappShareUrl(share)}>
            {t('team.shareWhatsapp')}
          </LinkButton>
          <Button
            variant="tonal"
            icon={iconCopy}
            onClick={() => {
              void navigator.clipboard?.writeText(created.link);
              snack({ text: t('team.copied') });
            }}
          >
            {t('team.copyLink')}
          </Button>
          {created.whatsappQueued ? <Notice tone="info">{t('team.whatsappQueued')}</Notice> : null}
          <p className="k-t-body-sm k-muted">{inviteMessage(share)}</p>
          <Button variant="text" onClick={close}>
            {t('app.done')}
          </Button>
        </div>
      )}
    </BottomSheet>
  );
}
